import { ChangeDetectionStrategy, Component } from '@angular/core';
import {
  DynamicDialogRef,
  DialogService,
  DynamicDialogComponent,
} from 'primeng/dynamicdialog';
import { CourseService } from 'src/app/services/course.service';
import { NotificationService } from 'src/app/services/notification.service';
import { ListboxModule } from 'primeng/listbox';
import { ButtonModule } from 'primeng/button';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { user } from 'src/app/models/user.model';
import { Course } from 'src/app/models/course.model';

@Component({
  selector: 'app-course-users-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule, ListboxModule, ButtonModule],
  templateUrl: './course-users-dialog.html',
  styleUrl: './course-users-dialog.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CourseUsersDialog {
  instance?: DynamicDialogComponent;

  // Übergebene Daten aus dem Dialog-Aufruf
  course?: Course;
  availableUsers?: user[];
  courseUsers?: user[];
  // Nicht angezeigt, beim Speichern aber erhalten (sonst aus Kurs entfernt)
  private hiddenCourseUsers: user[] = [];

  selectedAvailableUsers: user[] = [];
  selectedCourseUsers: user[] = [];

  constructor(
    private readonly ref: DynamicDialogRef,
    private readonly dialogService: DialogService,
    private readonly courseService: CourseService,
    private readonly notificationService: NotificationService,
  ) {
    this.instance = this.dialogService.getInstance(this.ref);
    if (this.instance && this.instance.data) {
      this.course = this.instance.data['course'];
      this.availableUsers = this.instance.data['availableUsers'];
      const users = (this.instance.data['course'] as Course).users ?? [];
      // Kurs-User ohne Rollen (GetAllCourses) → Abgleich mit bereits gefilterten availableUsers
      const participantIds = new Set(this.availableUsers.map((u) => u.userId));
      this.courseUsers = users.filter((u) => participantIds.has(u.userId));
      this.hiddenCourseUsers = users.filter((u) => !participantIds.has(u.userId));

      this.filterAvailableUsers();
      this.sortArray(this.availableUsers);
      this.sortArray(this.courseUsers);
    }
  }

  closeDialog() {
    this.ref.close();
  }

  addUsers(): void {
    this.courseUsers = [...this.courseUsers, ...this.selectedAvailableUsers];
    this.filterAvailableUsers();
    this.selectedAvailableUsers = [];
    this.sortArray(this.courseUsers);
  }

  removeUsers(): void {
    this.availableUsers = [...this.availableUsers, ...this.selectedCourseUsers];
    this.courseUsers = this.courseUsers.filter(
      (user) => !this.selectedCourseUsers.includes(user),
    );
    this.selectedCourseUsers = [];
    this.sortArray(this.availableUsers);
  }

  updateCourseWithUserlist(): void {
    this.course.users = [...this.courseUsers, ...this.hiddenCourseUsers];
    this.courseService.updateCourse(this.course).subscribe({
      next: (response) => {
        this.notificationService.success(
          `Teinehmer:innen von Kurs '${this.course.courseName}' erfolgreich geändert`,
          response,
        );
      },
      error: () => {
        this.notificationService.error(
          `Teilnehmer:innen von Kurs '${this.course.courseName}' NICHT geändert`,
        );
      },
      complete: () => {
        this.ref.close();
      },
    });
  }

  private filterAvailableUsers(): void {
    const courseUserIds = this.courseUsers.map((user) => user.userId);
    this.availableUsers = this.availableUsers.filter(
      (user) => !courseUserIds.includes(user.userId),
    );
  }

  private sortArray(array: user[]): void {
    array.sort((a, b) => a.lastname.localeCompare(b.lastname));
  }
}
